import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Quote } from "lucide-react";
import { Container, Eyebrow, Icon, Reveal, SectionHeading, ArrowFwd } from "../components/shared/Shared";
import { useLang } from "../context/LanguageContext";

export const ServiceSteps = () => {
  const { t, c } = useLang();
  const s = t.sections;
  return (
    <section className="relative overflow-hidden bg-brand-navy py-24 text-white" data-testid="service-steps-section">
      <div className="absolute inset-0 hero-grid opacity-30" />
      <div className="absolute -end-32 top-0 h-[520px] w-[520px] rounded-full bg-brand-teal/20 blur-3xl" />
      <Container className="relative">
        <SectionHeading light eyebrow={s.deliverEyebrow} title={s.deliverTitle} description={s.deliverDesc} />
        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-5">
          {c.SERVICE_STEPS.map((st, i) => (
            <Reveal key={st.number} delay={i * 90} className="group relative rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur transition-colors duration-300 hover:border-brand-orange/60 hover:bg-white/10">
              <span className="font-heading text-5xl font-bold text-white/10 transition-colors group-hover:text-brand-orange/70" dir="ltr">{st.number}</span>
              <h3 className="mt-4 font-heading text-lg font-bold leading-snug text-white">{st.title}</h3>
              <ul className="mt-4 flex flex-wrap gap-1.5">{st.items.map((it) => <li key={it} className="rounded-md bg-white/10 px-2 py-1 text-[11px] font-medium text-slate-200">{it}</li>)}</ul>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
};

export const TMTSection = () => {
  const { t, c } = useLang();
  return (
    <section className="py-24" data-testid="tmt-section">
      <Container className="grid items-center gap-14 lg:grid-cols-2">
        <Reveal className="relative">
          <div className="overflow-hidden rounded-3xl shadow-2xl shadow-brand-navy/10"><img src={c.IMAGES.servers} alt="Data centre" className="aspect-[4/3] w-full object-cover" /></div>
          <div className="absolute -bottom-8 -end-4 rounded-2xl bg-brand-orange p-6 text-white shadow-xl sm:-end-8">
            <p className="font-heading text-4xl font-bold">TMT</p>
            <p className="text-sm text-orange-50">{t.sections.focusedExpertise}</p>
          </div>
        </Reveal>
        <div>
          <SectionHeading eyebrow={t.sections.industryFocus} title={c.TMT.title} description={c.TMT.description} />
          <Reveal delay={120} className="mt-8 grid gap-4 sm:grid-cols-2">
            {c.WHY_US.map((w) => (
              <div key={w.title} className="flex gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-teal/10 text-brand-teal"><Icon name={w.icon} className="h-5 w-5" /></span>
                <div><p className="font-semibold text-brand-navy">{w.title}</p><p className="mt-1 text-sm text-slate-500">{w.description}</p></div>
              </div>
            ))}
          </Reveal>
        </div>
      </Container>
    </section>
  );
};

export const Products = () => {
  const { t, c } = useLang();
  const s = t.sections;
  return (
    <section className="bg-slate-50 py-24" data-testid="products-section">
      <Container>
        <SectionHeading eyebrow={s.productsEyebrow} title={s.productsTitle} description={s.productsDesc} align="center" />
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {c.PRODUCTS.map((p, i) => (
            <Reveal key={p.name} delay={(i % 4) * 80} className="card-hover group rounded-2xl bg-white p-7 shadow-sm ring-1 ring-slate-100">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-navy text-white transition-colors group-hover:bg-brand-orange"><Icon name={p.icon} className="h-6 w-6" /></span>
              <h3 className="mt-6 font-heading text-xl font-bold text-brand-navy">{p.name}</h3>
              <p className="mt-1 text-sm text-slate-500">{p.subtitle}</p>
              <Link to="/contact" className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-brand-teal opacity-0 transition-opacity group-hover:opacity-100">{s.learnMore} <ArrowUpRight className="h-4 w-4 rtl:-scale-x-100" /></Link>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
};

export const IndustriesGrid = ({ compact = false }) => {
  const { t, c } = useLang();
  const s = t.sections;
  return (
    <section className="py-24" data-testid="industries-section">
      <Container>
        {!compact && <SectionHeading eyebrow={s.industriesEyebrow} title={s.industriesTitle} description={s.industriesDesc} />}
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {c.INDUSTRIES.map((ind, i) => (
            <Reveal key={ind.slug} delay={(i % 4) * 80}>
              <Link to={`/industries/${ind.slug}`} data-testid={`industry-card-${ind.slug}`} className="group relative block aspect-[4/5] overflow-hidden rounded-2xl">
                <img src={ind.image} alt={ind.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/40 to-transparent opacity-90 transition-opacity group-hover:opacity-95" />
                <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/15 backdrop-blur"><Icon name={ind.icon} className="h-5 w-5" /></span>
                  <h3 className="mt-4 font-heading text-xl font-bold leading-snug">{ind.title}</h3>
                  <p className="mt-2 max-h-0 overflow-hidden text-sm text-slate-200 opacity-0 transition-all duration-500 group-hover:max-h-24 group-hover:opacity-100">{ind.summary}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-orange-300">{s.explore} <ArrowFwd className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
};

export const GroupSection = () => {
  const { t, c } = useLang();
  const s = t.sections;
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#EEF6F8] to-white py-24" data-testid="group-section">
      <Container>
        <SectionHeading eyebrow={s.groupEyebrow} title={s.groupTitle} description={c.GROUP.intro} align="center" />
        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          {c.GROUP.companies.map((co, i) => (
            <Reveal key={co.name} delay={i * 120} className="card-hover relative overflow-hidden rounded-3xl bg-white p-8 shadow-sm ring-1 ring-slate-100 sm:p-10">
              <span className={`absolute end-0 top-0 h-32 w-32 -translate-y-1/2 translate-x-1/2 rtl:-translate-x-1/2 rounded-full ${i === 0 ? "bg-brand-orange/15" : "bg-brand-teal/15"}`} />
              <Eyebrow>{co.region}</Eyebrow>
              <h3 className="mt-4 font-heading text-2xl font-bold text-brand-navy">{co.name}</h3>
              <p className="mt-1 text-sm font-medium text-slate-500">{co.location}</p>
              <p className="mt-5 leading-relaxed text-slate-600">{co.description}</p>
              <div className="mt-6 flex flex-wrap gap-2">{co.highlights.map((h) => <span key={h} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{h}</span>)}</div>
              <p className="mt-6 text-sm font-semibold text-brand-teal" dir="ltr">{co.website}</p>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10 text-center"><Link to="/group" className="btn-outline">{s.aboutGroup} <ArrowFwd className="h-4 w-4" /></Link></Reveal>
      </Container>
    </section>
  );
};

export const Testimonials = () => {
  const { t, c } = useLang();
  return (
    <section className="py-24" data-testid="testimonials-section">
      <Container>
        <SectionHeading eyebrow={t.sections.testimonialsEyebrow} title={t.sections.testimonialsTitle} align="center" />
        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {c.TESTIMONIALS.map((x, i) => (
            <Reveal key={x.company} delay={i * 100} className="card-hover relative rounded-3xl bg-white p-8 shadow-sm ring-1 ring-slate-100">
              <Quote className="h-8 w-8 text-brand-orange/40" />
              <p className="mt-4 leading-relaxed text-slate-700">&ldquo;{x.quote}&rdquo;</p>
              <div className="mt-6 border-t border-slate-100 pt-5"><p className="font-semibold text-brand-navy">{x.name}</p><p className="text-sm text-slate-500">{x.company}</p></div>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
};

export const Resources = () => {
  const { t, c } = useLang();
  return (
    <section className="bg-slate-50 py-24" data-testid="resources-section">
      <Container>
        <SectionHeading eyebrow={t.sections.resourcesEyebrow} title={t.sections.resourcesTitle} />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {c.RESOURCES.map((r, i) => (
            <Reveal key={r.title} delay={i * 80}>
              <Link to="/blogs" className="card-hover group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-7">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-teal/10 text-brand-teal transition-colors group-hover:bg-brand-teal group-hover:text-white"><Icon name={r.icon} className="h-6 w-6" /></span>
                <h3 className="mt-6 font-heading text-xl font-bold text-brand-navy">{r.title}</h3>
                <p className="mt-2 flex-1 text-sm text-slate-500">{r.description}</p>
                <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-brand-orange">{t.sections.explore} <ArrowFwd className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" /></span>
              </Link>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
};

const HomeSections = () => (
  <>
    <ServiceSteps />
    <TMTSection />
    <Products />
    <IndustriesGrid />
    <GroupSection />
    <Testimonials />
    <Resources />
  </>
);

export default HomeSections;
